import { useSearchParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { RouteContext } from "../../../contexts/RoutesContext";
import { AppContext } from "../../../contexts/AppContext";
import { LoadingTableContent } from "../Shared/LoadingTableContent";
import { PositionDetailsModel, PositionsModel } from "../../../apis/ApisPositions";
import Select from 'react-select'
import { PositionTableHeader } from "./PositionTableHeader";
import { PositionTableBody } from "./PositionTableBody";
import { PositionCards } from "./PositionCards";
import { FilterOption } from "../Shared/SharedTypes";
import { departmentPrefixedStyle, groupByPrefixedStyle, locationPrefixedStyle, rolePrefixedStyle } from "./PositionDropDownStyles";
import { UserType } from "../../Variables";

const groupByOptions: FilterOption[] = [
    { value: 'Department', label: 'Department' },
    { value: 'Location', label: 'Location' },
    { value: 'Role', label: 'Role' },
]

export type PositionsFilteredProps = {
    intra: boolean,
    positions: PositionsModel,
}

export function PositionsFiltered(props: PositionsFilteredProps) {
    const app = useContext(AppContext);
    const [queryParameters] = useSearchParams();
    const [positions, setPositions] = useState<PositionDetailsModel[]>([]);
    const [departmentFilters, setDepartmentFilters] = useState<FilterOption[]>([]);
    const [roleFilters, setRoleFilters] = useState<FilterOption[]>([]);
    const [locationFilters, setLocationFilters] = useState<FilterOption[]>([]);

    let b = queryParameters.get("b");
    let d = queryParameters.get("d");
    let r = queryParameters.get("r");
    let l = queryParameters.get("l");
    let jcc = queryParameters.get("jcc");

    let positionProps = props.positions.Positions;
    const [defaultGroupingFilter, setDefaultGroupingFilter] = useState<number>(b == null ? (r != null ? 2 : l != null ? 1 : 0) : (b == "l" ? 1 : b == "r" ? 2 : 0));
    const [departmentFilter, setDepartmentFilter] = useState<number>(0);
    const [roleFilter, setRoleFilter] = useState<number>(0);
    const [locationFilter, setLocationFilter] = useState<number>(0);

    const filterCheck = (rFilters: FilterOption[], dFilters: FilterOption[], lFilters: FilterOption[]) => {
        if (!app.loading) {
            var rmatch = rFilters.findIndex(x => x.label == r);
            var lmatch = lFilters.findIndex(x => x.label == l);
            var dmatch = dFilters.findIndex(x => x.label == d);

            if (rmatch == -1)
                rmatch = 0;
            if (lmatch == -1)
                lmatch = 0;
            if (dmatch == -1)
                dmatch = 0;

            setRoleFilter(rmatch);
            setLocationFilter(lmatch);
            setDepartmentFilter(dmatch);
        }
    }

    const setUsingRole = (updateFilters: boolean) => {
        setDefaultGroupingFilter(2);
        if (!app.loading) {
            setPositions(positions.sort((a, b) => a.RoleOrder - b.RoleOrder));
            if (updateFilters)
                filterCheck(roleFilters, departmentFilters, locationFilters);
        }
    }

    const setUsingLocation = (updateFilters: boolean) => {
        setPositions(positions.sort((a, b) => a.LocationOrder - b.LocationOrder));
        if (!app.loading) {
            setDefaultGroupingFilter(1);
            if (updateFilters)
                filterCheck(roleFilters, departmentFilters, locationFilters);
        }
    }

    const setUsingDepartment = (updateFilters: boolean) => {
        setDefaultGroupingFilter(0);
        if (!app.loading) {
            setPositions(positions.sort((a, b) => a.DepartmentOrder - b.DepartmentOrder));
            if (updateFilters)
                filterCheck(roleFilters, departmentFilters, locationFilters);
        }
    }

    const setUsingCategory = (updateFilters: boolean) => {
        setDefaultGroupingFilter(0);
        if (!app.loading) {
            setPositions(positions.sort((a, b) => a.DepartmentOrder - b.DepartmentOrder));
            if (updateFilters)
                filterCheck(roleFilters, departmentFilters, locationFilters);
        }
    }

    useEffect(() => {
        if (r != null) {
            setUsingRole(true);
        }
    }, [r]);

    useEffect(() => {
        if (l != null) {
            setUsingLocation(true);
        }
    }, [l]);

    useEffect(() => {
        if (d != null) {
            setUsingDepartment(true);
        }
    }, [d]);

    useEffect(() => {
        if (jcc != null && props.positions.JobCategories.findIndex(x => x.Code == jcc) != -1) {
            setUsingCategory(true);
        }
        else if(queryParameters.has("jcc"))
        {
            setUsingCategory(true);
        }
    }, [jcc]);

    let PqeAndExpView = props.positions != null && props.positions.Features.JobBoard_ShowPQE && props.positions.Features.JobBoard_ShowExperience;
    let PqeView = props.positions != null && props.positions.Features.JobBoard_ShowPQE && !props.positions.Features.JobBoard_ShowExperience;
    let ExpView = props.positions != null && !props.positions.Features.JobBoard_ShowPQE && props.positions.Features.JobBoard_ShowExperience;
    let RefView = props.positions != null && props.positions.Features.JobBoard_ShowRef;
    let PracticeGroupView = props.positions != null && props.positions.Features.JobBoard_PracticeGroup;
    let totalCols = 3; // 3 is default

    if (PqeAndExpView)
        totalCols++;
    if (PqeView)
        totalCols++;
    if (ExpView)
        totalCols++;
    if (RefView)
        totalCols++;
    if (PracticeGroupView)
        totalCols++;

    const routeContext = useContext(RouteContext);
    useEffect(() => {
        if (!props.intra) {
            if (!app.loading && app.userLoggedIn && app.loggedInUser.UserType == UserType.Candidate) {
                routeContext.setRoutes([{ name: "Positions list", route: null }]);
            }
            else {
                routeContext.setRoutes([]);
            }
        }
        else {
            routeContext.setRoutes([{ name: "Intranet positions", route: null }]);
        }

        if (!app.loading) {
            const dFilters = [{ value: '', label: 'Any' }, ...props.positions.Departments.map(x => { return ({ value: x, label: x }) })];
            const rFilters = [{ value: '', label: 'Any' }, ...props.positions.Roles.map(x => { return ({ value: x, label: x }) })];
            const lFilters = [{ value: '', label: 'Any' }, ...props.positions.Locations.map(x => { return ({ value: x, label: x }) })];
            setDepartmentFilters(dFilters);
            setRoleFilters(rFilters);
            setLocationFilters(lFilters);

            switch (defaultGroupingFilter) {
                case 0:
                    setPositions(positionProps.sort((a, b) => a.DepartmentOrder - b.DepartmentOrder));
                    break;
                case 1:
                    setPositions(positionProps.sort((a, b) => a.LocationOrder - b.LocationOrder));
                    break;
                case 2:
                    setPositions(positionProps.sort((a, b) => a.RoleOrder - b.RoleOrder));
                    break;
            }

            filterCheck(rFilters, dFilters, lFilters);
        }

    }, [app.loading]);

    return (<>
        <div className="row row-tight">
            <Select className="col-lg-3 col-md-6 mb-3"
                isDisabled={app.loading}
                options={groupByOptions}
                styles={groupByPrefixedStyle(app.styleColourSecondary, app.styleColourAlternative)}
                value={groupByOptions[defaultGroupingFilter]}
                onChange={(option: any) => {
                    switch (groupByOptions.indexOf(option)) {
                        case 0:
                            setUsingDepartment(false);
                            break;
                        case 1:
                            setUsingLocation(false);
                            break;
                        case 2:
                            setUsingRole(false);
                            break;
                    }
                }}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: app.styleColourSecondary,
                        primary: app.styleColourSecondary,
                    },
                })}
                aria-label="Group by" />
            <Select className="col-lg-3 col-md-6 mb-3" isDisabled={app.loading}
                aria-label="Department filter"
                onChange={(option: any) => {
                    var idx = departmentFilters.indexOf(option);
                    setDepartmentFilter(idx);
                }}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: app.styleColourSecondary,
                        primary: app.styleColourSecondary,
                    },
                })}
                value={departmentFilters[departmentFilter]} options={departmentFilters} styles={departmentPrefixedStyle(app.styleColourSecondary, app.styleColourAlternative)} />
            <Select className="col-lg-3 col-md-6 mb-3" isDisabled={app.loading}
                aria-label="Role filter"
                onChange={(option: any) => {
                    var idx = roleFilters.indexOf(option);
                    setRoleFilter(idx);
                }}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: app.styleColourSecondary,
                        primary: app.styleColourSecondary,
                    },
                })}
                value={roleFilters[roleFilter]} options={roleFilters} styles={rolePrefixedStyle(app.styleColourSecondary, app.styleColourAlternative)} />
            <Select className="col-lg-3 col-md-6 mb-3" isDisabled={app.loading}
                aria-label="Location filter"
                onChange={(option: any) => {
                    var idx = locationFilters.indexOf(option);
                    setLocationFilter(idx);
                }}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary25: app.styleColourSecondary,
                        primary: app.styleColourSecondary,
                    },
                })}
                value={locationFilters[locationFilter]}
                options={locationFilters}
                styles={locationPrefixedStyle(app.styleColourSecondary, app.styleColourAlternative)} />
        </div>

        <div className="card shadow widescreen-only">
            <div className="overflow-scroll-table">
                <table className="ListingTable table table-hover mt-0 mb-0">
                    <thead className="table-secondary no-highlight">
                        <LoadingTableContent loading={app.loading} colspan={totalCols} classes="text-center vertical-center">
                            <PositionTableHeader totalCols={totalCols} RefView={RefView} PracticeGroupView={PracticeGroupView} PqeView={PqeView} ExpView={ExpView} PqeAndExpView={PqeAndExpView} />
                        </LoadingTableContent>
                    </thead>
                    <tbody>
                        <LoadingTableContent colspan={totalCols} loading={app.loading} classes="text-center">
                            <PositionTableBody
                                intra={props.intra}
                                roleFilter={roleFilters != null && roleFilters.length > 0 ? roleFilters[roleFilter].value : null}
                                locationFilter={locationFilters != null && locationFilters.length > 0 ? locationFilters[locationFilter].value : null}
                                departmentFilter={departmentFilters != null && departmentFilters.length > 0 ? departmentFilters[departmentFilter].value : null}
                                grouping={defaultGroupingFilter}
                                positions={positions.filter(x => jcc == null || jcc == '' || x.CategoryCode == jcc)} totalCols={totalCols} RefView={RefView} PracticeGroupView={PracticeGroupView} PqeView={PqeView} ExpView={ExpView} PqeAndExpView={PqeAndExpView} />
                        </LoadingTableContent>
                    </tbody>
                </table>
            </div>
        </div>
        <div className="mob-only">
            {<PositionCards
                intra={props.intra}
                roleFilter={roleFilters != null && roleFilters.length > 0 ? roleFilters[roleFilter].value : null}
                locationFilter={locationFilters != null && locationFilters.length > 0 ? locationFilters[locationFilter].value : null}
                departmentFilter={departmentFilters != null && departmentFilters.length > 0 ? departmentFilters[departmentFilter].value : null}
                positions={positions.filter(x => jcc == null || x.CategoryCode == jcc)} RefView={RefView} PracticeGroupView={PracticeGroupView} PqeView={PqeView} ExpView={ExpView} PqeAndExpView={PqeAndExpView}
            ></PositionCards>}
        </div>
    </>);
}